import React, { Suspense } from 'react'
import { AuthAlert } from './AuthAlert'

interface AuthCardProps {
  title: string
  subtitle?: string
  children: React.ReactNode
  footer?: React.ReactNode
  /** Show URL param alerts (info/error/warning/success) above the form content */
  showParamAlerts?: boolean
}

/**
 * AuthCard — consistent card shell for all auth forms.
 * Layout mirrors FormCard: icon + title header, card-site shell, space-y-8 vertical rhythm.
 *
 * Pass `showParamAlerts` to automatically show URL param messages (info/error/warning/success)
 * above the form content, inside the card.
 */
export function AuthCard({ title, subtitle, children, footer, showParamAlerts }: AuthCardProps) {
  return (
    <div className="">
      {/* Header: icon + title, matching FormCard layout */}
      <div className="flex items-center gap-8 justify-center text-center mb-3">
        <div>
          <h1 className="text-2xl lg:text-[28px] font-semibold leading-tight text-brand-black">
            {title}
          </h1>
          {subtitle && <p className="m-1 text-brand-muted text-base">{subtitle}</p>}
        </div>
      </div>

      {/* URL param alerts rendered ABOVE form content */}
      {showParamAlerts && (
        <div className="mb-4">
          <Suspense fallback={null}>
            <AuthAlert />
          </Suspense>
        </div>
      )}

      {/* Form content */}
      <div>{children}</div>

      {/* Optional footer links */}
      {footer && <div className="text-center text-sm p-2 text-brand-muted">{footer}</div>}
    </div>
  )
}
